import { useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Icon from '@mui/material/Icon';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { ENDPOINT } from 'constants';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import ComplexStatisticsCard from 'examples/Cards/StatisticsCards/ComplexStatisticsCard';

const STATUS_COLOR = {
  delivered: 'success',
  failed: 'error',
  skipped: 'secondary',
  'cancelled due to payment': 'info',
};

const TYPE_LABEL = {
  renewal: 'Renewal',
  due: 'Due date',
  overdue: 'Overdue',
  cart: 'Abandoned cart',
  order: 'Order payment',
  subscription: 'Subscription',
};

const toDateInput = (date) => date.toISOString().slice(0, 10);

// Pure content — no DashboardLayout/Navbar of its own. Embedded as the default tab in
// the Reminder Settings hub (src/layouts/reminderSettings/index.jsx), the same way
// OverView.jsx is embedded in the Email Templates hub.
const ReminderLogs = () => {
  const { user } = useSelector((state) => state.auth);
  const tableRef = useRef(null);

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  const [filters, setFilters] = useState({ status: '', channel: '', type: '', from: '', to: '' });

  const fetchSummary = async () => {
    try {
      const { data } = await axios.get(`${ENDPOINT}api/v1/admin/reminder/logs/summary`, {
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });
      setSummary(data.data.last7Days);
    } catch (err) {
      console.error('Failed to fetch reminder log summary:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 25 };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const { data } = await axios.get(`${ENDPOINT}api/v1/admin/reminder/logs`, {
        params,
        headers: { Authorization: `Bearer ${user?.accessToken}` },
      });
      setLogs(data.data.logs || []);
      setTotal(data.data.total || 0);
      setTotalPages(data.data.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch reminder logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSummary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLogs(); }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (key) => (e) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // Clicking a summary/overview card jumps straight to the matching detailed log
  // list below — clears every other filter first so the card reads as "show me
  // exactly this bucket", then scrolls the results into view.
  const applyQuickFilter = (patch) => {
    setPage(1);
    setFilters({ status: '', channel: '', type: '', from: '', to: '', ...patch });
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const columns = [
    { Header: 'User', accessor: 'userName', width: '22%' },
    { Header: 'Type', accessor: 'type', width: '16%' },
    { Header: 'Channel', accessor: 'channel', width: '12%' },
    { Header: 'Sent at', accessor: 'sentAt', width: '25%' },
    { Header: 'Status', accessor: 'status', width: '15%' },
  ];

  const rows = useMemo(
    () =>
      logs.map((log) => ({
        userName: (
          <MDBox>
            <MDTypography variant="button" fontWeight="medium" display="block">
              {log.user ? `${log.user.firstName} ${log.user.lastName}` : 'Unknown user'}
            </MDTypography>
            {log.user?.email && (
              <MDTypography variant="caption" color="secondary">{log.user.email}</MDTypography>
            )}
          </MDBox>
        ),
        type: <MDTypography variant="caption">{TYPE_LABEL[log.type] || log.type}</MDTypography>,
        channel: (
          <Chip
            icon={<Icon fontSize="small">{log.channel === 'sms' ? 'sms' : 'mail'}</Icon>}
            label={log.channel}
            size="small"
            variant="outlined"
          />
        ),
        sentAt: (
          <MDTypography variant="caption" fontFamily="monospace">
            {new Date(log.sentAt).toLocaleString()}
          </MDTypography>
        ),
        status: (
          <Chip label={log.status} size="small" color={STATUS_COLOR[log.status] || 'default'} />
        ),
      })),
    [logs],
  );

  const summaryCards = summary
    ? [
        {
          key: 'sent',
          color: 'info',
          icon: 'send',
          title: 'Sent (last 7 days)',
          count: String(summary.total),
          percentage: { color: 'secondary', amount: '', label: 'across all reminder jobs' },
          onClick: () => applyQuickFilter({ from: toDateInput(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)), to: toDateInput(new Date()) }),
        },
        {
          key: 'delivered',
          color: 'success',
          icon: 'mark_email_read',
          title: 'Delivered',
          count: String(summary.byStatus?.delivered || 0),
          percentage: { color: 'secondary', amount: '', label: 'successful sends' },
          onClick: () => applyQuickFilter({ status: 'delivered' }),
        },
        {
          key: 'failed',
          color: 'error',
          icon: 'error_outline',
          title: 'Failed',
          count: String(summary.byStatus?.failed || 0),
          percentage: { color: 'secondary', amount: '', label: 'after one retry' },
          onClick: () => applyQuickFilter({ status: 'failed' }),
        },
        {
          key: 'sms',
          color: 'dark',
          icon: 'sms',
          title: 'Sent via SMS',
          count: String(summary.byChannel?.sms || 0),
          percentage: { color: 'secondary', amount: String(summary.byChannel?.email || 0), label: 'via email' },
          onClick: () => applyQuickFilter({ channel: 'sms' }),
        },
        {
          key: 'due',
          color: 'warning',
          icon: 'event',
          title: 'Due Reminders',
          count: String(summary.byType?.due || 0),
          percentage: { color: 'secondary', amount: '', label: 'due date reminders sent' },
          onClick: () => applyQuickFilter({ type: 'due' }),
        },
      ]
    : [];

  return (
    <MDBox>
      <MDTypography variant="caption" color="secondary" display="block" mb={2}>
        Every reminder actually sent, straight from the reminder_logs table — filter by user, date, status, or channel.
      </MDTypography>

      <Card sx={{ mb: 3 }}>
        <MDBox p={3} pb={1}>
          <MDTypography variant="h6">Filter</MDTypography>
        </MDBox>
        <MDBox px={3} pb={3} display="flex" flexWrap="wrap" gap={2}>
          <TextField
            select
            label="Status"
            size="small"
            value={filters.status}
            onChange={handleFilterChange('status')}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="skipped">Skipped</MenuItem>
            <MenuItem value="cancelled due to payment">Cancelled due to payment</MenuItem>
          </TextField>

          <TextField
            select
            label="Channel"
            size="small"
            value={filters.channel}
            onChange={handleFilterChange('channel')}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">All channels</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
          </TextField>

          <TextField
            select
            label="Type"
            size="small"
            value={filters.type}
            onChange={handleFilterChange('type')}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">All types</MenuItem>
            {Object.entries(TYPE_LABEL).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.from}
            onChange={handleFilterChange('from')}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filters.to}
            onChange={handleFilterChange('to')}
          />
        </MDBox>
      </Card>

      {summary && (
        <Grid container spacing={3} mb={1}>
          {summaryCards.map((s) => (
            <Grid item xs={12} md={6} lg={3} key={s.key}>
              <MDBox mb={1.5} onClick={s.onClick} sx={{ cursor: 'pointer' }}>
                <ComplexStatisticsCard
                  color={s.color}
                  icon={s.icon}
                  title={s.title}
                  count={s.count}
                  percentage={s.percentage}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>
      )}

      <Card sx={{ mt: 2 }} ref={tableRef}>
        <MDBox px={2} pt={2} pb={2}>
          {loading ? (
            <MDBox display="flex" justifyContent="center" py={5}>
              <CircularProgress size={28} />
            </MDBox>
          ) : rows.length === 0 ? (
            <MDBox py={5} textAlign="center">
              <MDTypography variant="caption" color="text">
                No reminders match these filters.
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                canSearch={false}
                noEndBorder
              />
              <MDBox display="flex" justifyContent="space-between" alignItems="center" px={1} pt={1}>
                <MDTypography variant="caption" color="secondary">
                  Page {page} of {totalPages} — {total} total
                </MDTypography>
                <MDBox display="flex" gap={1}>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </MDButton>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </MDButton>
                </MDBox>
              </MDBox>
            </>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
};

export default ReminderLogs;
